import React, { useState } from 'react';
import type { DoorViewSide } from '../surfaces/doorViewSide';
import { createDefaultDoorConfiguration } from '../types/doorConfiguration';
import { DesignSummary } from './DesignSummary';
import { DesignerHeader } from './DesignerHeader';
import { LiveDoorPreview } from './LiveDoorPreview';
import { PreviewViewControls } from './PreviewViewControls';
import { ViewSideToggle } from './ViewSideToggle';
import { StageNavBar } from './StageNavBar';
import { StageProgress } from './StageProgress';
import {
  DOOR_DESIGNER_STAGES,
  getDoorDesignerStage,
  isLastDoorDesignerStage,
} from './stages';
import { ColourStage } from './stages/ColourStage';
import { DoorStyleStage } from './stages/DoorStyleStage';
import { FurnitureStage } from './stages/FurnitureStage';
import { commitDimension, FrameStage } from './stages/FrameStage';
import { GlassStage } from './stages/GlassStage';
import { ReviewStage } from './stages/ReviewStage';
import {
  DEV_DOOR_HEIGHT_MAX_MM,
  DEV_DOOR_HEIGHT_MIN_MM,
  DEV_DOOR_WIDTH_MAX_MM,
  DEV_DOOR_WIDTH_MIN_MM,
} from '../pages/developmentPreviewBounds';
import { TooltipProvider } from '../ui/tooltip';

function createInitialConfiguration() {
  const configuration = createDefaultDoorConfiguration();
  return {
    ...configuration,
    ancillaries: {
      ...configuration.ancillaries,
      hasThreshold: true,
    },
  };
}

export const DoorDesignerApp: React.FC<{
  /** JARVIS host only. Public embed should omit this control. */
  hardwareDebugToggle?: boolean;
}> = ({ hardwareDebugToggle = false }) => {
  const [configuration, setConfiguration] = useState(createInitialConfiguration);
  const [stageIndex, setStageIndex] = useState(0);
  const [viewSide, setViewSide] = useState<DoorViewSide>('external');
  const [hardwareDebug, setHardwareDebug] = useState(false);
  const [widthInput, setWidthInput] = useState(String(configuration.dimensions.overallWidthMm));
  const [heightInput, setHeightInput] = useState(String(configuration.dimensions.overallHeightMm));

  const stage = getDoorDesignerStage(stageIndex);
  const isLastStage = isLastDoorDesignerStage(stageIndex);

  const commitWidth = (raw: string, constrain: boolean) => {
    const next = commitDimension(
      raw,
      constrain,
      DEV_DOOR_WIDTH_MIN_MM,
      DEV_DOOR_WIDTH_MAX_MM,
      configuration.dimensions.overallWidthMm
    );
    if (next == null) {
      if (constrain) setWidthInput(String(configuration.dimensions.overallWidthMm));
      else setWidthInput(raw);
      return;
    }
    setWidthInput(String(next));
    setConfiguration((current) => ({
      ...current,
      dimensions: { ...current.dimensions, overallWidthMm: next },
    }));
  };

  const commitHeight = (raw: string, constrain: boolean) => {
    const next = commitDimension(
      raw,
      constrain,
      DEV_DOOR_HEIGHT_MIN_MM,
      DEV_DOOR_HEIGHT_MAX_MM,
      configuration.dimensions.overallHeightMm
    );
    if (next == null) {
      if (constrain) setHeightInput(String(configuration.dimensions.overallHeightMm));
      else setHeightInput(raw);
      return;
    }
    setHeightInput(String(next));
    setConfiguration((current) => ({
      ...current,
      dimensions: { ...current.dimensions, overallHeightMm: next },
    }));
  };

  const startAgain = () => {
    const next = createInitialConfiguration();
    setConfiguration(next);
    setStageIndex(0);
    setViewSide('external');
    setWidthInput(String(next.dimensions.overallWidthMm));
    setHeightInput(String(next.dimensions.overallHeightMm));
  };

  return (
    <TooltipProvider>
    <div
      className="door-designer"
      data-door-designer-root
      style={{ height: 'calc(100vh - 96px)', maxHeight: 'calc(100vh - 96px)' }}
    >
        <div className="flex h-full min-h-[inherit] max-h-[inherit] flex-col overflow-hidden">
          <DesignerHeader />
          <StageProgress stageIndex={stageIndex} onSelectStage={setStageIndex} />
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-hidden p-3 md:grid-cols-[minmax(17rem,22%)_minmax(0,1fr)_minmax(15rem,20%)]">
            <section className="order-2 flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-card md:order-1">
              <div className="border-b border-border px-4 py-3">
                <h2 className="text-base font-semibold uppercase tracking-wide">
                  {stageIndex + 1}. {stage.stepperLabel}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">{stage.description}</p>
              </div>
              <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
                {stage.id === 'frame' ? (
                  <FrameStage
                    configuration={configuration}
                    widthInput={widthInput}
                    heightInput={heightInput}
                    onWidthInput={commitWidth}
                    onHeightInput={commitHeight}
                    onChange={setConfiguration}
                  />
                ) : null}
                {stage.id === 'style' ? (
                  <DoorStyleStage configuration={configuration} onChange={setConfiguration} />
                ) : null}
                {stage.id === 'colour' ? (
                  <ColourStage
                    configuration={configuration}
                    viewSide={viewSide}
                    onViewSideChange={setViewSide}
                    onChange={setConfiguration}
                  />
                ) : null}
                {stage.id === 'glass' ? (
                  <GlassStage configuration={configuration} onChange={setConfiguration} />
                ) : null}
                {stage.id === 'furniture' ? (
                  <FurnitureStage configuration={configuration} onChange={setConfiguration} />
                ) : null}
                {stage.id === 'review' ? (
                  <ReviewStage configuration={configuration} onEditStage={setStageIndex} />
                ) : null}
              </div>
            </section>

            <section className="order-1 flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-card md:order-2">
              <div className="flex items-center justify-center border-b border-border px-3 py-2">
                <ViewSideToggle
                  viewSide={viewSide}
                  onChange={setViewSide}
                  ariaLabel="Preview face"
                  variant="preview"
                />
              </div>
              <div className="min-h-[240px] flex-1 overflow-hidden bg-muted/40 p-4 md:min-h-0">
                <LiveDoorPreview
                  configuration={configuration}
                  viewSide={viewSide}
                  debugHardwareGuides={hardwareDebug}
                />
              </div>
              <PreviewViewControls
                showDebugToggle={hardwareDebugToggle}
                debugEnabled={hardwareDebug}
                onDebugChange={setHardwareDebug}
              />
            </section>

            <div className="order-3 min-h-0 min-w-0">
              <DesignSummary configuration={configuration} onStartAgain={startAgain} />
            </div>
          </div>
          <StageNavBar
            stageIndex={stageIndex}
            isLastStage={isLastStage}
            onExit={startAgain}
            onBack={() => setStageIndex((current) => Math.max(0, current - 1))}
            onContinue={() =>
              setStageIndex((current) => Math.min(DOOR_DESIGNER_STAGES.length - 1, current + 1))
            }
          />
        </div>
      </div>
    </TooltipProvider>
  );
};
